/**
 * TemplatesBrowserDialog — Phase 4 vertical starter packs picker.
 *
 * Opened from the ProjectHub "+ Nuovo ▾" split-button (entry: "Da
 * template"). The user picks a vertical (top filter strip), then a
 * template card, then customises the project name before instantiating.
 * On success the dialog closes and the page navigates to the freshly-
 * created project's Detail page.
 *
 * Catalog is fetched via ``useProjectTemplates`` (10min staleTime —
 * curated server-side, doesn't change between renders). The clone
 * mutation lives in ``useCreateProjectFromTemplate`` and invalidates
 * ``projectsQueryKey`` so the Hub grid picks up the new entry without
 * a manual refetch.
 *
 * Verticals shipped in Phase 4 (mirror the BE seed):
 *   - Commercialista (Italian SMB Tier 1)
 *   - PMI
 *   - Studio Legale
 *   - Ingegneria (technical / CAD)
 *   - Architettura
 *   - Sales
 *   - Marketing
 *
 * Adding a new vertical is one-line BE seed + one-line FE label below.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  Loader2,
  Sparkles,
} from "lucide-react";
import * as React from "react";

import type { ProjectTemplate } from "@ando/core/api";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SurfaceSkeleton,
  cn,
  toast,
} from "@ando/ui";

import {
  useCreateProjectFromTemplate,
  useProjectTemplates,
} from "../hooks/useProjects.js";
import { ProjectIcon } from "./ProjectIcon.js";

export interface TemplatesBrowserDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

interface VerticalDef {
  readonly slug: string;
  readonly label: MessageDescriptor;
  readonly sublabel: MessageDescriptor;
}

/**
 * Vertical labels — keep in sync with the BE seed values. ``All`` is
 * a synthetic entry the dialog adds so the user can scan every starter
 * pack on first open. Labels/sublabels are localisable ``msg``
 * descriptors resolved via ``i18n._()`` at render.
 */
const VERTICALS: ReadonlyArray<VerticalDef> = [
  { slug: "__all__", label: msg`Tutti`, sublabel: msg`Tutti i template` },
  { slug: "commercialista", label: msg`Commercialista`, sublabel: msg`IVA, bilancio, contabilità` },
  { slug: "pmi", label: msg`PMI`, sublabel: msg`Cash, controllo, finance` },
  { slug: "studio-legale", label: msg`Studio Legale`, sublabel: msg`Pratiche forensi, PEC` },
  { slug: "studio-notarile", label: msg`Studio Notarile`, sublabel: msg`Atti, repertorio, antiriciclaggio` },
  { slug: "ingegneria", label: msg`Ingegneria`, sublabel: msg`CAD, tavole tecniche` },
  { slug: "architettura", label: msg`Architettura`, sublabel: msg`Cantiere, direzione lavori` },
  { slug: "sales", label: msg`Sales`, sublabel: msg`Pipeline, prospect, CRM` },
  { slug: "marketing", label: msg`Marketing`, sublabel: msg`Editoriale, lead nurturing` },
  { slug: "e-commerce", label: msg`E-commerce`, sublabel: msg`Order ops, customer care` },
  { slug: "customer-success", label: msg`Customer Success`, sublabel: msg`Retention, QBR, upsell` },
  { slug: "hr", label: msg`HR / People`, sublabel: msg`Onboarding, recruiting, policy` },
  { slug: "it-devops", label: msg`IT / DevOps`, sublabel: msg`Incident, runbook, SRE` },
];

export const TemplatesBrowserDialog: React.FC<TemplatesBrowserDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [activeVertical, setActiveVertical] = React.useState<string>("__all__");
  const [selected, setSelected] = React.useState<ProjectTemplate | null>(null);
  const [customName, setCustomName] = React.useState("");

  // Reset internal state on close so re-opening always lands on the
  // catalog. Done in an effect so cancel + reopen never shows the prior
  // selection ghost.
  React.useEffect(() => {
    if (!open) {
      setActiveVertical("__all__");
      setSelected(null);
      setCustomName("");
    }
  }, [open]);

  const query = useProjectTemplates(
    activeVertical === "__all__" ? undefined : activeVertical,
  );
  const mutation = useCreateProjectFromTemplate();

  const handleInstantiate = React.useCallback(() => {
    if (!selected || mutation.isPending) return;
    const trimmed = customName.trim();
    mutation.mutate(
      {
        slug: selected.slug,
        name: trimmed.length > 0 ? trimmed : undefined,
      },
      {
        onSuccess: (created) => {
          toast.success(
            i18n._(msg`Progetto "${created.name}" creato`),
          );
          onOpenChange(false);
          void navigate({
            to: "/workspace/projects/$projectId",
            params: { projectId: created.project_id },
          });
        },
        onError: (err) => {
          toast.error(
            err instanceof Error
              ? i18n._(msg`Impossibile creare il progetto: ${err.message}`)
              : i18n._(msg`Impossibile creare il progetto`),
          );
        },
      },
    );
  }, [selected, customName, mutation, navigate, onOpenChange, i18n]);

  const templates = query.data ?? [];

  // Two-step UX: pick → confirm. The footer changes accordingly so the
  // primary CTA always reads as the next action.
  const inConfirmStep = selected !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        ariaTitle={i18n._(msg`Nuovo progetto da template`)}
      >
        <DialogHeader>
          <DialogTitle>
            {inConfirmStep ? (
              <span className="inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label={i18n._(msg`Torna al catalogo`)}
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-md",
                    "text-ink-3 hover:bg-ink-1/[0.04] hover:text-ink-1",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                  )}
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <Trans>Conferma e crea</Trans>
              </span>
            ) : (
              <Trans>Nuovo progetto da template</Trans>
            )}
          </DialogTitle>
          <DialogDescription>
            {inConfirmStep ? (
              <Trans>
                Personalizza il nome del progetto prima di crearlo. Persona,
                istruzioni e capability richieste verranno copiate dal template.
              </Trans>
            ) : (
              <Trans>
                Parti da uno starter pack curato per ruolo. Ogni template
                imposta una persona dell'agente, le capability necessarie e i
                primi suggerimenti operativi.
              </Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        {inConfirmStep && selected ? (
          <ConfirmStep
            template={selected}
            name={customName}
            onNameChange={setCustomName}
          />
        ) : (
          <CatalogStep
            verticals={VERTICALS}
            activeVertical={activeVertical}
            onVerticalChange={setActiveVertical}
            templates={templates}
            isLoading={query.isLoading}
            isError={query.isError}
            onSelect={(t) => {
              setSelected(t);
              setCustomName(t.name);
            }}
          />
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            <Trans>Annulla</Trans>
          </Button>
          {inConfirmStep ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleInstantiate}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="h-3.5 w-3.5" aria-hidden />
              )}
              <Trans>Crea progetto</Trans>
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

TemplatesBrowserDialog.displayName = "TemplatesBrowserDialog";

// ───────────────────────────────────────────────────────────────────────────
// Catalog step
// ───────────────────────────────────────────────────────────────────────────

interface CatalogStepProps {
  readonly verticals: ReadonlyArray<VerticalDef>;
  readonly activeVertical: string;
  readonly onVerticalChange: (slug: string) => void;
  readonly templates: readonly ProjectTemplate[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onSelect: (t: ProjectTemplate) => void;
}

const CatalogStep: React.FC<CatalogStepProps> = ({
  verticals,
  activeVertical,
  onVerticalChange,
  templates,
  isLoading,
  isError,
  onSelect,
}) => {
  const { i18n } = useLingui();
  return (
    <div className="flex flex-col gap-4">
      {/* Vertical strip — horizontal scroller on mobile, wrap on desktop. */}
      <div className="-mx-1 flex flex-wrap gap-1.5 overflow-x-auto px-1 pb-1">
        {verticals.map((v) => {
          const isActive = activeVertical === v.slug;
          return (
            <button
              key={v.slug}
              type="button"
              onClick={() => onVerticalChange(v.slug)}
              aria-pressed={isActive}
              title={i18n._(v.sublabel)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 font-medium text-[11.5px] transition-colors",
                isActive
                  ? "bg-ink-1 text-white"
                  : "bg-transparent text-ink-2 hover:bg-white/60 hover:text-ink-1",
              )}
            >
              {i18n._(v.label)}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {isLoading ? (
        <SurfaceSkeleton variant="card-grid" count={4} />
      ) : isError ? (
        <AlertBanner tone="error">
          <Trans>
            Impossibile caricare i template. Riprova fra poco.
          </Trans>
        </AlertBanner>
      ) : templates.length === 0 ? (
        <div className="rounded-md border border-hairline border-dashed bg-card/40 px-4 py-6 text-center text-[12.5px] text-ink-2 leading-relaxed">
          <Trans>
            Nessun template per questa categoria. Prova con un'altra o crea un
            progetto vuoto.
          </Trans>
        </div>
      ) : (
        <div className="grid max-h-[420px] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
          {templates.map((t) => (
            <button
              key={t.template_id}
              type="button"
              onClick={() => onSelect(t)}
              aria-label={i18n._(msg`Usa template: ${t.name}`)}
              className={cn(
                "group flex flex-col gap-2 rounded-xl border border-hairline bg-white/70 p-3.5 text-left shadow-sm",
                "transition-colors hover:bg-ink-1/[0.03]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              <div className="flex items-start gap-2.5">
                <ProjectIcon
                  iconName={t.icon}
                  colorToken={t.color}
                  size={14}
                  className="h-9 w-9 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-[550] text-[13px] text-ink-1 leading-tight">
                    {t.name}
                  </div>
                  <div className="mt-0.5 line-clamp-2 text-[11.5px] text-ink-3 leading-snug">
                    {t.description}
                  </div>
                </div>
              </div>
              {t.required_capabilities.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {t.required_capabilities.slice(0, 4).map((c) => (
                    <span
                      key={c}
                      className={cn(
                        "inline-flex items-center rounded-full px-1.5 py-0.5 font-medium text-[10px]",
                        "bg-ink-1/[0.05] text-ink-3",
                      )}
                    >
                      {c}
                    </span>
                  ))}
                  {t.required_capabilities.length > 4 ? (
                    <span className="inline-flex items-center text-[10px] text-ink-3">
                      +{t.required_capabilities.length - 4}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Confirm step
// ───────────────────────────────────────────────────────────────────────────

const ConfirmStep: React.FC<{
  readonly template: ProjectTemplate;
  readonly name: string;
  readonly onNameChange: (s: string) => void;
}> = ({ template, name, onNameChange }) => {
  const { i18n } = useLingui();
  const personaInstructions = template.persona_seed.instructions?.trim() ?? "";
  const personaRole = template.persona_seed.persona_role?.trim() ?? "";
  const personaTone = template.persona_seed.persona_tone?.trim() ?? "";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-xl border border-hairline bg-white/70 p-3.5">
        <ProjectIcon
          iconName={template.icon}
          colorToken={template.color}
          size={14}
          className="h-9 w-9 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="font-[550] text-[13px] text-ink-1 leading-tight">
            {template.name}
          </div>
          <div className="mt-1 text-[12px] text-ink-2 leading-snug">
            {template.long_description ?? template.description}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="project-template-name"
          className="font-medium text-[12px] text-ink-2"
        >
          <Trans>Nome del progetto</Trans>
        </label>
        <input
          id="project-template-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          maxLength={255}
          placeholder={i18n._(msg`Es. IVA Q2 2026 — Rossi SRL`)}
          aria-label={i18n._(msg`Nome del nuovo progetto`)}
          className={cn(
            "h-9 rounded-md border border-hairline bg-white/85 px-3",
            "text-[13px] text-ink-1 placeholder:text-ink-3",
            "focus-visible:border-ando-orange/45 focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
          )}
        />
        <div className="text-[11px] text-ink-3">
          <Trans>
            Lascia il nome del template oppure personalizzalo per il singolo
            cliente / commessa.
          </Trans>
        </div>
      </div>

      {(personaInstructions || personaRole || personaTone) ? (
        <div className="flex flex-col gap-1.5">
          <div className="font-medium text-[12px] text-ink-2">
            <Trans>Cosa viene applicato</Trans>
          </div>
          <div className="space-y-2 rounded-md border border-hairline bg-ink-1/[0.02] px-3 py-2.5 text-[12px] text-ink-2 leading-relaxed">
            {personaRole ? (
              <div>
                <span className="font-medium text-ink-1">
                  <Trans>Ruolo:</Trans>
                </span>{" "}
                {personaRole}
                {personaTone ? <span className="ml-1 text-ink-3">· {personaTone}</span> : null}
              </div>
            ) : null}
            {personaInstructions ? (
              <div className="line-clamp-3 italic">{personaInstructions}</div>
            ) : null}
            {template.required_capabilities.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <Sparkles className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
                <span className="text-[11px] text-ink-3">
                  <Trans>Capability suggerite:</Trans>
                </span>
                {template.required_capabilities.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center rounded-full bg-ink-1/[0.05] px-1.5 py-0.5 font-medium text-[10px] text-ink-3"
                  >
                    {c}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
